/* eslint-disable no-nested-ternary */
/* eslint-disable no-nested-ternary */
/* eslint-disable */
/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-inferrable-types */
import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet';
import { pairsApi, programsApi } from 'api';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { dateFormat } from 'utils/dateFormat';
import { useProgramPanel } from 'contexts/programPanelContext';
import { Box, BoxSection } from 'components/_new/Box';
import { Avatar } from 'components/_new/Avatar';
import { Spinner } from 'components/_new/Spinner';
import { classNames } from 'primereact/utils';
import { parseHtml } from 'utils/htmlParser';
import { prepareUserAreas } from 'utils/prepareUserAreas';
import OptionItem from 'components/userPanel/pairing/OptionItem';
import CustomConfirmDialog from 'components/_common/ConfirmDialog';
import { userDisplayName } from '../../utils/userDisplayName';
import { getPairStatusInfo } from './utils';
import { Button } from '../_new/Button';
import { DialogAction } from '../_new/Dialog';
import { Dropdown } from '../_common/forms/Dropdown';

import passedIcon from 'assets/icons/new/passed.svg';
import failedIcon from 'assets/icons/new/failed.svg';

const ProgramPairDetails = () => {
	const { t } = useTranslation();

	const { data: program, isLoading, isSuccess } = useProgramPanel();

	const params = useParams<{ pairId: string }>();
	const pairId = Number(params.pairId);
	const [pairStatusModalOpen, setPairStatusModalOpen] = useState<boolean>(false);
	const [selectedValue, setSelectedValue] = useState<string>();
	const [isHistoryExpanded, setIsHistoryExpanded] = useState<boolean>(false);

	const [signatureConfirmDialogOpen, setSignatureConfirmDialogOpen] = useState<boolean>(false);
	const [signatureAction, setSignatureAction] = useState<{
		type: 'cancel' | 'restore';
		target: 'contract' | 'plan';
		role: 'mentee' | 'mentor';
		message: string;
	} | null>(null);

	const {
		data: programPair,
		isSuccess: getProgramPairSuccess,
		isLoading: getProgramPairLoading,
		refetch: refetchProgramPair,
	} = useQuery(
		['getProgramPair', { programId: program?.id, pairId }],
		() => programsApi.getProgramAdminPair({ programId: Number(program?.id), pairId }),
		{
			enabled: Boolean(isSuccess && pairId),
		}
	);

	const {
		data: pairLastChange,
		isLoading: isLoadingLastChange,
		refetch: refetchPairLastChange,
	} = useQuery(
		['getPairLastChange', { programId: program?.id, pairId }],
		() => pairsApi.getPairLastChange({ programId: Number(program?.id), pairId }),
		{
			enabled: Boolean(isSuccess && pairId),
		}
	);

	const { data: pairChangelogHistory, isLoading: isLoadingHistory } = useQuery(
		['getPairChangelogHistory', { programId: program?.id, pairId }],
		() => pairsApi.getPairChangelogHistory({ programId: Number(program?.id), pairId }),
		{
			enabled: Boolean(isHistoryExpanded && isSuccess && pairId),
		}
	);

	const { mutate: changePairStatus, isLoading: isChangingPairStatus } = useMutation(pairsApi.adminChangePairStatus, {
		onSuccess: (data) => {
			refetchProgramPair();
			setSelectedValue(undefined);
			setPairStatusModalOpen(false);
			refetchPairLastChange();
		},
	});

	const { mutate: manageContractSignature, isLoading: isManagingSignature } = useMutation(
		(payload: {
			programId: number;
			pairId: number;
			action: 'cancel' | 'restore';
			target: 'contract' | 'plan';
			role: 'mentee' | 'mentor';
		}) => {
			return pairsApi.adminManageSignature(payload);
		},
		{
			onSuccess: () => {
				refetchProgramPair();
				setSignatureConfirmDialogOpen(false);
				setSignatureAction(null);
			},
		}
	);

	const statusPairs = [
		{ label: t('programPanel.pair.list.status.accepted'), value: 'accepted', id: 1 },
		{ label: t('programPanel.pair.list.status.rejected'), value: 'rejected', id: 2 },
		{ label: t('programPanel.pair.list.status.finished'), value: 'finished', id: 3 },
		{ label: t('programPanel.pair.list.status.removed'), value: 'removed', id: 4 },
	];

	const handleSubmitForm = (event: { preventDefault: () => void }) => {
		event.preventDefault();
		if (selectedValue && program) {
			const newStatus = {
				programId: program.id,
				pairId,
				payload: selectedValue,
			};
			changePairStatus(newStatus);
		}
	};

	const handleSignatureAction = (
		type: 'cancel' | 'restore',
		target: 'contract' | 'plan',
		role: 'mentee' | 'mentor'
	) => {
		const userName = role === 'mentee' ? menteeData?.name : mentorData?.name;
		const targetName =
			target === 'contract' ? t('userPanel.navigation.contract') : t('userPanel.navigation.mentoringPlan');

		let message = '';
		if (type === 'cancel') {
			message = t('programPanel.editContract.confirmCancel', {
				user: userName,
				target: targetName,
			});
		} else {
			message = t('programPanel.editContract.confirmRestore', {
				user: userName,
				target: targetName,
			});
		}

		setSignatureAction({ type, target, role, message });
		setSignatureConfirmDialogOpen(true);
	};

	const handleConfirmSignatureAction = () => {
		if (signatureAction && program) {
			manageContractSignature({
				programId: program.id,
				pairId,
				action: signatureAction.type,
				target: signatureAction.target,
				role: signatureAction.role,
			});
		}
	};

	const getEventTypeLabel = (eventType: string): string => {
		const labels: Record<string, string> = {
			pair_proposed: t('programPanel.pair.changelog.pairProposed'),
			pair_accepted_by_admin: t('programPanel.pair.changelog.pairAcceptedByAdmin'),
			pair_status_changed: t('programPanel.pair.changelog.pairStatusChanged'),
		};
		return labels[eventType] || eventType;
	};

	const getStatusLabel = (status: string): string => {
		const labels: Record<string, string> = {
			open: t('programPanel.pair.list.status.open'),
			accepted: t('programPanel.pair.list.status.accepted'),
			rejected: t('programPanel.pair.list.status.rejected'),
			finished: t('programPanel.pair.list.status.finished'),
			removed: t('programPanel.pair.list.status.removed'),
		};
		return labels[status] || status;
	};

	const renderChangeItem = (change: any, isLast = false) => {
		const adminName = change.admin ? `${change.admin.firstName} ${change.admin.lastName}` : t('misc.unknown');

		return (
			<div
				key={change.id}
				className={classNames('flex align-items-start gap-3 py-2', {
					'border-bottom-1 border-gray-200': !isLast,
				})}
			>
				<div className="flex flex-column align-items-center" style={{ width: '24px' }}>
					<div className="bg-primary border-circle" style={{ width: '8px', height: '8px' }} />
					{!isLast && <div className="bg-gray-300" style={{ width: '2px', flex: 1, marginTop: '4px' }} />}
				</div>
				<div className="flex-1">
					<div className="flex justify-content-between align-items-center mb-1">
						<span className="font-semibold text-900">{getEventTypeLabel(change.eventType)}</span>
						<span className="text-sm text-500">{dateFormat(change.createdAt, 'full')}</span>
					</div>

					{change.previousStatus && change.newStatus && (
						<div className="text-sm text-600 mb-1">
							{getStatusLabel(change.previousStatus)} → {getStatusLabel(change.newStatus)}
						</div>
					)}

					<div className="text-sm text-500 flex align-items-center gap-1">
						<i className="pi pi-user text-xs" />
						<span>{adminName}</span>
					</div>
				</div>
			</div>
		);
	};

	const renderChangelogSection = () => {
		if (isLoadingLastChange) {
			return <span className="text-sm text-500">{t('misc.loading')}...</span>;
		}

		if (!pairLastChange) {
			return null;
		}

		const hasMoreChanges = pairChangelogHistory && pairChangelogHistory.length > 1;

		return (
			<div className="p-3 bg-gray-50 border-round">
				<div className="flex align-items-center justify-content-between">
					<div className="flex align-items-center gap-2 flex-1">
						<i className="pi pi-info-circle text-primary" />
						<div className="text-sm">
							<span className="font-semibold">{t('programPanel.pair.lastChange')}:</span>{' '}
							{getEventTypeLabel(pairLastChange.eventType)}
							{pairLastChange.previousStatus && pairLastChange.newStatus && (
								<>
									{' '}
									<span className="text-600">
										({getStatusLabel(pairLastChange.previousStatus)} →{' '}
										{getStatusLabel(pairLastChange.newStatus)})
									</span>
								</>
							)}
							{' · '}
							<span className="font-medium">
								{pairLastChange.admin.firstName} {pairLastChange.admin.lastName}
							</span>
							{' · '}
							<span className="text-500">{dateFormat(pairLastChange.createdAt, 'full')}</span>
						</div>
					</div>

					{hasMoreChanges && (
						<Button
							variant="white-text"
							variantSize="sm"
							icon={isHistoryExpanded ? 'chevron-up' : 'chevron-down'}
							iconSet="pi"
							onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
							label={
								isHistoryExpanded
									? t('programPanel.pair.hideHistory')
									: t('programPanel.pair.showHistory', {
										count: (pairChangelogHistory?.length || 1) - 1,
									})
							}
						/>
					)}
				</div>

				{isHistoryExpanded && hasMoreChanges && (
					<div className="mt-3 pt-3 border-top-1 border-gray-200">
						{isLoadingHistory ? (
							<div className="flex justify-content-center p-3">
								<Spinner />
							</div>
						) : (
							<>
								<div className="text-sm font-semibold text-600 mb-3">
									{t('programPanel.pair.previousChanges')}:
								</div>
								<div className="flex flex-column">
									{pairChangelogHistory
										.slice(1)
										.map((change, index, array) =>
											renderChangeItem(change, index === array.length - 1)
										)}
								</div>
							</>
						)}
					</div>
				)}
			</div>
		);
	};

	const renderSessionDuration = (session: any): string => {
		if (!session.date) {
			return '-';
		}
		if (session.duration) {
			return `${session.duration} min`;
		}
		if (programPair?.contract?.sessionTime) {
			return `${t('programPanel.pair.accordingToContract')} (${programPair.contract.sessionTime} min)`;
		}
		return '-';
	};

	const renderSignatureButton = (isSigned: boolean, target: 'contract' | 'plan', role: 'mentee' | 'mentor') => {
		if (isSigned) {
			return (
				<Button
					variantSize="sm"
					variant="rose-text"
					onClick={() => handleSignatureAction('cancel', target, role)}
					disabled={isManagingSignature}
					label={t('programPanel.editContract.cancelSignature')}
					iconSet="pi"
					icon="times"
				/>
			);
		}
		return (
			<Button
				variantSize="sm"
				variant="primary-text"
				onClick={() => handleSignatureAction('restore', target, role)}
				disabled={isManagingSignature}
				label={t('programPanel.editContract.markAsSigned')}
				iconSet="pi"
				icon="check"
			/>
		);
	};

	const getOptions = () => {
		const menteeAreas = programPair?.menteeApplication?.systemAnswers?.areas
			? prepareUserAreas(programPair.menteeApplication.systemAnswers.areas)
			: [];

		const mentorAreas = programPair?.mentorApplication?.systemAnswers?.areas
			? prepareUserAreas(programPair.mentorApplication.systemAnswers.areas)
			: [];

		const separatedAreas: any[] = [];

		menteeAreas?.forEach((area) => {
			const isMentorMatch = mentorAreas?.find((mentorArea) => mentorArea === area);
			if (isMentorMatch) {
				return separatedAreas.unshift({ area, match: true });
			}
			return separatedAreas.push({ area, match: false });
		});
		return separatedAreas;
	};

	const menteeData = useMemo(() => {
		if (programPair?.menteeApplication?.systemAnswers) {
			const systemAnswers = programPair?.menteeApplication?.systemAnswers;
			const { position, expectations, areas, department } = systemAnswers;
			return {
				applicationId: programPair.menteeApplicationId,
				avatar: programPair.menteeMembership.user.avatarActive
					? programPair.menteeMembership.user.avatar
					: undefined,
				name: userDisplayName(programPair.menteeMembership.user),
				avatarName: userDisplayName(programPair.menteeMembership.user, null),
				email: programPair.menteeMembership.user.email,
				position,
				expectations,
				areas,
				department,
				lastLoginDate: programPair.menteeMembership.user.lastLoginDate
					? dateFormat(programPair.menteeMembership.user.lastLoginDate, 'full')
					: '-',
			};
		}
		return null;
	}, [programPair]);

	const mentorData = useMemo(() => {
		if (programPair?.mentorApplication?.systemAnswers) {
			const systemAnswers = programPair?.mentorApplication?.systemAnswers;
			const { position, expectations, areas, department } = systemAnswers;
			return {
				applicationId: programPair.mentorApplicationId,
				avatar: programPair.mentorMembership.user.avatarActive
					? programPair.mentorMembership.user.avatar
					: undefined,
				name: userDisplayName(programPair.mentorMembership.user),
				avatarName: userDisplayName(programPair.mentorMembership.user, null),
				email: programPair.mentorMembership.user.email,
				position,
				expectations,
				areas,
				department,
				lastLoginDate: programPair.mentorMembership.user.lastLoginDate
					? dateFormat(programPair.mentorMembership.user.lastLoginDate, 'full')
					: '-',
			};
		}
		return null;
	}, [programPair]);

	const renderContent = (content: any, separator = ', ') => {
		if (content && Array.isArray(content)) {
			return content.join(separator);
		}
		if (content && String(content).length > 0) {
			return parseHtml(content);
		}
		return null;
	};

	const questionnairesData = useMemo(() => {
		const midGoals = Object.values(programPair?.questionnaires?.middle?.goals || {});
		const lastGoals = Object.values(programPair?.questionnaires?.last?.goals || {});

		const mappedGoals = (programPair?.goals || []).map((goal, index) => {
			const baseGoal = {
				key: `goal-${index}`,
				name: t('programPanel.goalRealizationNo', { no: index + 1 }),
				mid: `${midGoals[index] ? `${midGoals[index]}/10` : t('misc.noData')}`,
				last: `${lastGoals[index] ? `${lastGoals[index]}/10` : t('misc.noData')}`,
				showDetails: false,
				goalContent: '',
				measurableOutcome: '',
			};

			if (program?.adminShowGoals) {
				return {
					...baseGoal,
					goalContent: goal.goal,
					measurableOutcome: goal.change,
					showDetails: true,
				};
			}

			return baseGoal;
		});

		const mappedQuestionnaires = [
			{
				key: 'menteeEngagement',
				name: t('programPanel.questionaireHeaders.menteeEngagement'),
				mid: programPair?.questionnaires?.middle?.menteeEngagement
					? `${programPair?.questionnaires?.middle?.menteeEngagement}/10`
					: t('misc.noData'),
				last: programPair?.questionnaires?.last?.menteeEngagement
					? `${programPair?.questionnaires?.last?.menteeEngagement}/10`
					: t('misc.noData'),
				showDetails: false,
				goalContent: '',
				measurableOutcome: '',
			},
			{
				key: 'mentorEngagement',
				name: t('programPanel.questionaireHeaders.mentorEngagement'),
				mid: programPair?.questionnaires?.middle?.mentorEngagement
					? `${programPair?.questionnaires?.middle?.mentorEngagement}/10`
					: t('misc.noData'),
				last: programPair?.questionnaires?.last?.mentorEngagement
					? `${programPair?.questionnaires?.last?.mentorEngagement}/10`
					: t('misc.noData'),
				showDetails: false,
				goalContent: '',
				measurableOutcome: '',
			},
			{
				key: 'menteeSatisfaction',
				name: t('programPanel.questionaireHeaders.menteeSatisfaction'),
				mid: programPair?.questionnaires?.middle?.menteeSatisfaction
					? `${programPair?.questionnaires?.middle?.menteeSatisfaction}/10`
					: t('misc.noData'),
				last: programPair?.questionnaires?.last?.menteeSatisfaction
					? `${programPair?.questionnaires?.last?.menteeSatisfaction}/10`
					: t('misc.noData'),
				showDetails: false,
				goalContent: '',
				measurableOutcome: '',
			},
			{
				key: 'processStandardsMaintained',
				name: t('programPanel.questionaireHeaders.processStandardsMaintained'),
				mid:
					programPair?.questionnaires?.middle?.standards !== undefined
						? programPair?.questionnaires?.middle?.standards
							? t('misc.yes')
							: t('misc.no')
						: t('misc.noData'),
				last:
					programPair?.questionnaires?.last?.standards !== undefined
						? programPair?.questionnaires?.last?.standards
							? t('misc.yes')
							: t('misc.no')
						: t('misc.noData'),
				showDetails: false,
				goalContent: '',
				measurableOutcome: '',
			},
			{
				key: 'meetingSchedule',
				name: t('programPanel.questionaireHeaders.meetingSchedule'),
				mid:
					programPair?.questionnaires?.middle?.schedule !== undefined
						? programPair?.questionnaires?.middle?.schedule
							? t('misc.yes')
							: t('misc.no')
						: t('misc.noData'),
				last:
					programPair?.questionnaires?.last?.schedule !== undefined
						? programPair?.questionnaires?.last?.schedule
							? t('misc.yes')
							: t('misc.no')
						: t('misc.noData'),
				showDetails: false,
				goalContent: '',
				measurableOutcome: '',
			},
			{
				key: 'contractRulesFollowed',
				name: t('programPanel.questionaireHeaders.contractRulesFollowed'),
				mid:
					programPair?.questionnaires?.middle?.contract !== undefined
						? programPair?.questionnaires?.middle?.contract
							? t('misc.yes')
							: t('misc.no')
						: t('misc.noData'),
				last:
					programPair?.questionnaires?.last?.contract !== undefined
						? programPair?.questionnaires?.last?.contract
							? t('misc.yes')
							: t('misc.no')
						: t('misc.noData'),
				showDetails: false,
				goalContent: '',
				measurableOutcome: '',
			},
		];

		return [...mappedGoals, ...mappedQuestionnaires];
	}, [programPair, program?.adminShowGoals, t]);

	const renderGoalsInDevelopmentPlan = () => {
		if (!program?.adminShowGoals || !programPair?.goals || programPair.goals.length === 0) {
			return null;
		}

		return (
			<div className="mt-4 mb-4">
				<div className="flex flex-column gap-3">
					{programPair.goals.map((goal, index) => (
						<div key={`goal-${goal.id}`} className="p-3 border-round bg-gray-50 flex ">
							<div className="w-3 align-self-center">{`${t('userPanel.goal.goal')} #${index + 1}`}:</div>
							<div>
								<div className="font-bold">{goal.goal}</div>
								<div className="text-sm color-gray mt-1">{goal.change}</div>
							</div>
						</div>
					))}
				</div>
			</div>
		);
	};

	const renderQuestionnaireTable = () => {
		return (
			<table className="p-datatable w-full">
				<thead className="p-datatable-thead">
					<tr>
						<th>{t('organizationPanel.questionBody')}</th>
						<th>{t('organizationPanel.questionnaireMid')}</th>
						<th>{t('organizationPanel.questionnaireLast')}</th>
					</tr>
				</thead>
				<tbody className="p-datatable-tbody">
					{questionnairesData.map((row) => (
						<React.Fragment key={row.key}>
							<tr>
								<td>
									{row.name}
									{row.showDetails && (
										<>
											<div className="text-sm mt-1 font-bold">{row.goalContent}</div>
											<div className="text-sm color-gray mt-1">{row.measurableOutcome}</div>
										</>
									)}
								</td>
								<td>{row.mid}</td>
								<td>{row.last}</td>
							</tr>
						</React.Fragment>
					))}
				</tbody>
			</table>
		);
	};

	return (
		<>
			<Helmet title={t('organizationPanel.programPairs')} />
			<CustomConfirmDialog
				confirmationOpen={signatureConfirmDialogOpen}
				setConfirmationOpen={setSignatureConfirmDialogOpen}
				handleAccept={handleConfirmSignatureAction}
				handleReject={() => {
					setSignatureConfirmDialogOpen(false);
					setSignatureAction(null);
				}}
				message={signatureAction?.message || ''}
			/>

			<SubPageTitle
				title={t('organizationPanel.programPairs')}
				additional={program?.displayName}
				right={() => (
					<Link to={`/program/${program?.name}/pair/list`} className="text-purplish-blue">
						{t('programPanel.pair.backToPairList')}
					</Link>
				)}
			/>
			<DialogAction
				title={t('userPanel.mentibotAi.reply.ratingComment.title')}
				visible={pairStatusModalOpen}
				onHide={() => {
					setPairStatusModalOpen(false);
				}}
				actions={[
					{
						key: 'save',
						form: 'pair-status-form',
						label: t('actions.save'),
						loading: isChangingPairStatus,
						submit: true,
						disabled: !selectedValue,
					},
				]}
			>
				<form
					id="pair-status-form"
					className="w-full flex flex-column justify-content-between"
					onSubmit={handleSubmitForm}
				>
					<p>{t('programPanel.pair.list.chooseNewPairStatus')}</p>
					<Dropdown
						placeholder={t('programPanel.pair.list.changePairStatus')}
						className="flex-1"
						value={selectedValue}
						onChange={(event) => {
							setSelectedValue(event.value);
						}}
						options={statusPairs}
						rounded
					/>
				</form>
			</DialogAction>
			{(isLoading || getProgramPairLoading) && <Spinner />}
			{program && isSuccess && getProgramPairSuccess && programPair && menteeData && mentorData && (
				<div className="flex flex-column gap-4">
					<Box variant="white-bordered">
						<BoxSection>
							<div className="flex flex-row flex-wrap gap-2">
								<div className="flex-1">
									<div className="color-gray mb-2">{t('misc.mentee')}:</div>
									<div className="flex flex-row justify-content-start gap-4 w-full">
										<Avatar src={menteeData?.avatar} name={menteeData.avatarName} size="lg" />
										<div>
											<Link
												to={`/program/${program.name}/application/${menteeData.applicationId}`}
												className={classNames('font-bold text-3xl strong text-wrap-any')}
											>
												{menteeData.name}
											</Link>
											{menteeData.position && (
												<div className="text-sm strong">{menteeData.position}</div>
											)}
											<div className="text-sm strong">{menteeData.email}</div>
											<div>
												{t('programPanel.lastLogin')}: {menteeData.lastLoginDate}
											</div>
										</div>
									</div>
								</div>
								<div className="flex-1">
									<div className="color-gray mb-2">{t('misc.mentor')}:</div>
									<div className="flex flex-row justify-content-start gap-4 w-full">
										<Avatar src={mentorData.avatar} name={mentorData.avatarName} size="lg" />
										<div>
											<Link
												to={`/program/${program.name}/application/${mentorData.applicationId}`}
												className="font-bold text-3xl strong text-wrap-any"
											>
												{mentorData.name}
											</Link>
											{mentorData.position && (
												<div className="text-sm strong">{mentorData.position}</div>
											)}
											<div className="text-sm strong">{mentorData.email}</div>
											<div>
												{t('programPanel.lastLogin')}: {mentorData.lastLoginDate}
											</div>
										</div>
									</div>
								</div>
							</div>
							<hr />
							<div className="mt-4">
								<div className="color-gray">{t('programPanel.pair.menteeGoals')}:</div>
								<div className="flex mt-4 gap-2 flex-wrap flex-start sm:flex-column md:flex-row">
									{getOptions().map((area) => (
										<OptionItem key={area.area} option={area} />
									))}
								</div>
							</div>
							<div className="my-4">
								<div className="color-gray">{t('programPanel.pair.menteeExpectations')}:</div>
								<div className="flex mt-1 gap-2 flex-wrap flex-start">
									{renderContent(menteeData.expectations)}
								</div>
							</div>
							<hr />
							<div className="flex justify-content-between align-items-center">
								<div className="flex flex-column gap-2 flex-1">
									<div className="pl-2 flex align-items-center gap-3">
										<span className="font-bold">
											{getPairStatusInfo(programPair)}
											{programPair.statusChangedAt && (
												<span className="ml-2 text-sm text-500">
													({dateFormat(programPair.statusChangedAt, 'full')})
												</span>
											)}
										</span>
									</div>

									{programPair.pairCoordinators.length > 0 && (
										<div className="mt-2">
											{t('programPanel.pair.pairCoordinator')}:{' '}
											<ul className="inline-flex flex-row gap-1 list-none strong">
												{programPair.pairCoordinators.map((coordinator) => (
													<li key={coordinator.id}>
														{coordinator?.programMembership?.user
															? userDisplayName(coordinator.programMembership.user)
															: null}
													</li>
												))}
											</ul>
										</div>
									)}
								</div>

								{programPair?.contractId && (
									<div>
										<Button
											onClick={() => setPairStatusModalOpen(true)}
											label={t('programPanel.pair.list.changePairStatus')}
										/>
									</div>
								)}
							</div>
							{renderChangelogSection()}
						</BoxSection>
					</Box>

					<Box variant="white-bordered">
						<BoxSection header={t('userPanel.navigation.contract')}>
							<div className="flex flex-column gap-4">
								<div className="flex flex-row">
									<div className="col-6">
										<div className="flex flex-column">
											<div className="flex align-items-center gap-2 mb-2">
												<span className="font-medium">
													{t('programPanel.pair.menteeSign')}:
												</span>
												{programPair?.contract?.initialSignDateMentee ? (
													<span className="flex align-items-center gap-2">
														<img src={passedIcon} alt="passed" style={{ width: 22, height: 22 }} />
														<span>
															{dateFormat(
																programPair?.contract?.initialSignDateMentee,
																'full'
															)}
														</span>
													</span>
												) : (
													<span className="flex align-items-center gap-2">
														<img src={failedIcon} alt="failed" style={{ width: 22, height: 22 }} />
														<span>-</span>
													</span>
												)}
											</div>
											<div>
												{renderSignatureButton(
													Boolean(programPair?.contract?.initialSignDateMentee),
													'contract',
													'mentee'
												)}
											</div>
										</div>
									</div>
									<div className="col-6">
										<div className="flex flex-column">
											<div className="flex align-items-center gap-2 mb-2">
												<span className="font-medium">
													{t('programPanel.pair.mentorSign')}:
												</span>
												{programPair?.contract?.initialSignDateMentor ? (
													<span className="flex align-items-center gap-2">
														<img src={passedIcon} alt="passed" style={{ width: 22, height: 22 }} />
														<span>
															{dateFormat(
																programPair?.contract?.initialSignDateMentor,
																'full'
															)}
														</span>
													</span>
												) : (
													<span className="flex align-items-center gap-2">
														<img src={failedIcon} alt="failed" style={{ width: 22, height: 22 }} />
														<span>-</span>
													</span>
												)}
											</div>
											<div>
												{renderSignatureButton(
													Boolean(programPair?.contract?.initialSignDateMentor),
													'contract',
													'mentor'
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</BoxSection>
					</Box>

					<Box variant="white-bordered">
						<BoxSection header={t('userPanel.navigation.mentoringPlan')}>
							<div className="flex flex-column gap-4">
								<div className="flex flex-row">
									<div className="col-6">
										<div className="flex flex-column">
											<div className="flex align-items-center gap-2 mb-2">
												<span className="font-medium">
													{t('programPanel.pair.menteeSign')}:
												</span>
												{programPair?.contract?.goalSignDateMentee ? (
													<span className="flex align-items-center gap-2">
														<img src={passedIcon} alt="passed" style={{ width: 22, height: 22 }} />
														<span>
															{dateFormat(
																programPair?.contract?.goalSignDateMentee,
																'full'
															)}
														</span>
													</span>
												) : (
													<span className="flex align-items-center gap-2">
														<img src={failedIcon} alt="failed" style={{ width: 22, height: 22 }} />
														<span>-</span>
													</span>
												)}
											</div>
											<div>
												{renderSignatureButton(
													Boolean(programPair?.contract?.goalSignDateMentee),
													'plan',
													'mentee'
												)}
											</div>
										</div>
									</div>
									<div className="col-6">
										<div className="flex flex-column">
											<div className="flex align-items-center gap-2 mb-2">
												<span className="font-medium">
													{t('programPanel.pair.mentorSign')}:
												</span>
												{programPair?.contract?.goalSignDateMentor ? (
													<span className="flex align-items-center gap-2">
														<img src={passedIcon} alt="passed" style={{ width: 22, height: 22 }} />
														<span>
															{dateFormat(
																programPair?.contract?.goalSignDateMentor,
																'full'
															)}
														</span>
													</span>
												) : (
													<span className="flex align-items-center gap-2">
														<img src={failedIcon} alt="failed" style={{ width: 22, height: 22 }} />
														<span>-</span>
													</span>
												)}
											</div>
											<div>
												{renderSignatureButton(
													Boolean(programPair?.contract?.goalSignDateMentor),
													'plan',
													'mentor'
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
							{renderGoalsInDevelopmentPlan()}
						</BoxSection>
					</Box>

					<Box variant="white-bordered">
						<BoxSection header={t('userPanel.progressSteps.steps.after_mid_session')}>
							<table className="p-datatable w-full">
								<thead className="p-datatable-thead">
									<tr>
										<th>{t('programPanel.number')}</th>
										<th>{t('programPanel.date')}</th>
										<th>{t('programPanel.pair.sessionDuration')}</th>
										<th>{t('programPanel.pair.menteeSessionRating')}</th>
										<th>{t('programPanel.pair.mentorSessionRating')}</th>
									</tr>
								</thead>
								<tbody className="p-datatable-tbody">
									{programPair.sessions?.map((session) => (
										<tr key={session.id}>
											<td>
												<div className="flex flex-row gap-2">
													<span>{session.sessionNumber}</span>
													{session.sessionType === 'middle' && (
														<span>{t('misc.middleSession')}</span>
													)}
												</div>
											</td>
											<td>
												{session.date ? (
													<div className="flex flex-row gap-2">
														<span>{dateFormat(session.date, 'full')}</span>
														{session.finished && (
															<i className="pi pi-check-circle text-green-400" />
														)}
													</div>
												) : (
													'-'
												)}
											</td>
											<td>{renderSessionDuration(session)}</td>
											<td>
												{session?.menteeRating ? (
													<div className="flex flex-column gap-1">
														<div>{`${session.menteeRating}/5`}</div>
														{session?.menteeRatingComment && (
															<div className="text-sm text-500">
																{session.menteeRatingComment}
															</div>
														)}
													</div>
												) : (
													'-'
												)}
											</td>
											<td>
												{session?.mentorRating ? (
													<div className="flex flex-column gap-1">
														<div>{`${session.mentorRating}/5`}</div>
														{session?.mentorRatingComment && (
															<div className="text-sm text-500">
																{session.mentorRatingComment}
															</div>
														)}
													</div>
												) : (
													'-'
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</BoxSection>
					</Box>

					<Box variant="white-bordered">
						<BoxSection header={t('organizationPanel.questionnaireHeader')}>
							{renderQuestionnaireTable()}
						</BoxSection>
					</Box>
				</div>
			)}
		</>
	);
};

export default ProgramPairDetails;
